import { useEffect, useMemo, useState } from 'react';

import { Columns2, Columns3, Columns4, Radio, Settings2, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useActiveContactsStore } from '@/stores/active-contacts.store';

import { getSavedFilters } from '../filters';

import { ChatCard } from './ChatCard';

const STORAGE_KEY = 'contacts-multi-chat-ids';
const GRID_SETTINGS_KEY = 'contacts-grid-settings';

interface GridSettings {
  columns: 2 | 3 | 4 | 'auto';
  maxCards: number;
  /** Auto-open chats for contacts with a live inbound session. */
  autoOpenLive: boolean;
}

const DEFAULT_SETTINGS: GridSettings = { columns: 'auto', maxCards: 8, autoOpenLive: false };

const getGridSettings = (): GridSettings => {
  try {
    const raw = localStorage.getItem(GRID_SETTINGS_KEY);
    return raw ? { ...DEFAULT_SETTINGS, ...JSON.parse(raw) } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
};

const saveGridSettings = (settings: GridSettings) => {
  localStorage.setItem(GRID_SETTINGS_KEY, JSON.stringify(settings));
};

export const getMultiChatIds = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const setMultiChatIds = (ids: string[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
};

export const addMultiChatId = (id: string) => {
  const ids = getMultiChatIds();
  if (!ids.includes(id)) {
    const updated = [...ids, id];
    setMultiChatIds(updated);
    return updated;
  }
  return ids;
};

/** Add at the front — used for deep links and auto-opened live sessions. */
export const addMultiChatIdToFront = (id: string) => {
  const ids = getMultiChatIds();
  if (!ids.includes(id)) {
    const updated = [id, ...ids];
    setMultiChatIds(updated);
    return updated;
  }
  return ids;
};

export const removeMultiChatId = (id: string) => {
  const ids = getMultiChatIds().filter((x) => x !== id);
  setMultiChatIds(ids);
  return ids;
};

const COLUMN_OPTIONS = [
  { value: 'auto' as const, label: 'Auto', icon: Settings2 },
  { value: 2 as const, label: '2 columns', icon: Columns2 },
  { value: 3 as const, label: '3 columns', icon: Columns3 },
  { value: 4 as const, label: '4 columns', icon: Columns4 },
];

const MAX_CARD_OPTIONS = [4, 6, 8, 12, 16];

interface MultiChatGridProps {
  contactIds: string[];
  onContactIdsChange: (ids: string[]) => void;
}

export const MultiChatGrid = ({ contactIds, onContactIdsChange }: MultiChatGridProps) => {
  const [settings, setSettings] = useState<GridSettings>(getGridSettings);
  const inboundLiveIds = useActiveContactsStore((s) => s.inboundIds);
  const inboundSessions = useActiveContactsStore((s) => s.inboundSessions);

  const updateSettings = (partial: Partial<GridSettings>) => {
    const next = { ...settings, ...partial };
    setSettings(next);
    saveGridSettings(next);
  };

  const handleClose = (id: string) => {
    const updated = removeMultiChatId(id);
    onContactIdsChange(updated);
  };

  // Dynamic placement: auto-open chats for live inbound sessions. Outbound
  // sessions never auto-open, and a saved channel filter (e.g. WhatsApp only)
  // restricts which live sessions may open. Opened cards stay until closed.
  useEffect(() => {
    if (!settings.autoOpenLive) return;
    const { channelFilter } = getSavedFilters();
    const missing = inboundSessions.filter(
      (s) => !contactIds.includes(s.id) && (!channelFilter || s.channel === channelFilter),
    );
    if (missing.length === 0) return;
    let updated = contactIds;
    for (const session of missing) {
      updated = addMultiChatIdToFront(session.id);
    }
    onContactIdsChange(updated);
  }, [settings.autoOpenLive, inboundSessions, contactIds, onContactIdsChange]);

  // Drag a contact from the list onto a card to swap the chat in place.
  const handleReplace = (currentId: string, newId: string) => {
    if (currentId === newId) return;
    const ids = getMultiChatIds();
    const to = ids.indexOf(currentId);
    if (to === -1) return;
    const from = ids.indexOf(newId);
    let updated: string[];
    if (from !== -1) {
      // Dragged contact is already open elsewhere — swap the two cards.
      updated = [...ids];
      updated[to] = newId;
      updated[from] = currentId;
    } else {
      updated = ids.map((id) => (id === currentId ? newId : id));
    }
    setMultiChatIds(updated);
    onContactIdsChange(updated);
  };

  // Live inbound chats are pinned first (stable order otherwise) so they
  // stay inside the visible maxCards slice.
  const orderedIds = useMemo(() => {
    if (inboundLiveIds.size === 0) return contactIds;
    const live = contactIds.filter((id) => inboundLiveIds.has(id));
    const rest = contactIds.filter((id) => !inboundLiveIds.has(id));
    return [...live, ...rest];
  }, [contactIds, inboundLiveIds]);

  const visibleIds = orderedIds.slice(0, settings.maxCards);

  if (contactIds.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-[#667781] dark:text-[#8696a0]">
        <Users className="h-10 w-10 opacity-20" />
        <p className="text-sm">Select contacts from the list to open chats</p>
      </div>
    );
  }

  const getGridCols = () => {
    if (settings.columns !== 'auto') {
      const map = { 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' };
      return map[settings.columns];
    }
    const count = visibleIds.length;
    if (count === 1) return 'grid-cols-1';
    if (count === 2) return 'grid-cols-2';
    if (count <= 4) return 'grid-cols-2';
    if (count <= 6) return 'grid-cols-2 xl:grid-cols-3';
    return 'grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4';
  };

  return (
    <div className="flex h-full flex-col bg-[#f0f2f5] dark:bg-[#111b21]">
      <div className="flex items-center justify-between border-b border-[#d1d7db] bg-[#f0f2f5] px-3 py-1.5 dark:border-[#2a3942] dark:bg-[#202c33]">
        <span className="text-xs text-[#667781] dark:text-[#8696a0]">
          {visibleIds.length} of {contactIds.length} chat{contactIds.length !== 1 ? 's' : ''}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-[#54656f] hover:bg-[#d9dbdf] hover:text-[#111b21] dark:text-[#aebac1] dark:hover:bg-[#2a3942] dark:hover:text-[#e9edef]"
            >
              <Settings2 className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel className="text-xs">Layout</DropdownMenuLabel>
            {COLUMN_OPTIONS.map((opt) => (
              <DropdownMenuItem
                key={opt.value}
                onClick={() => updateSettings({ columns: opt.value })}
                className={cn(settings.columns === opt.value && 'bg-accent')}
              >
                <opt.icon className="mr-2 h-3.5 w-3.5" />
                {opt.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs">Max chats</DropdownMenuLabel>
            {MAX_CARD_OPTIONS.map((n) => (
              <DropdownMenuItem
                key={n}
                onClick={() => updateSettings({ maxCards: n })}
                className={cn(settings.maxCards === n && 'bg-accent')}
              >
                {n} chats
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs">Live sessions</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => updateSettings({ autoOpenLive: !settings.autoOpenLive })}
              className={cn(settings.autoOpenLive && 'bg-accent')}
            >
              <Radio className="mr-2 h-3.5 w-3.5" />
              Auto-open live sessions
              {settings.autoOpenLive && <span className="ml-auto text-[10px]">on</span>}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className={`grid flex-1 gap-2 overflow-y-auto p-2 ${getGridCols()}`}>
        {visibleIds.map((id) => (
          <ChatCard key={id} contactId={id} onClose={handleClose} onReplace={handleReplace} />
        ))}
      </div>
    </div>
  );
};
